import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { useDispatch } from 'react-redux';
import { setUser } from '../slices/authSlice';
import { checkAuthStatus, initiateGoogleLogin, logoutUser } from '../api';
import { User } from '../../../types/auth.types';

interface GoogleLoginButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const GoogleLoginButton: React.FC<GoogleLoginButtonProps> = ({
  onSuccess,
  onError,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const handleLogin = async () => {
    setIsLoading(true);
    try {
      let user: User;
      try {
        user = await initiateGoogleLogin();
      } catch (firstError: any) {
        // First attempt failed: flush session and local cache, then force a fresh Google prompt once
        try {
          await logoutUser();
        } catch {}
        try {
          localStorage.removeItem('googleToken');
          localStorage.removeItem('tokenExpiry');
          localStorage.removeItem('lastAuthHttpStatus');
        } catch {}
        // Retry with forced prompt
        user = await initiateGoogleLogin({ forcePrompt: true });
      }

      const checkUser = await checkAuthStatus();
      if (!checkUser) {
        const lastStatus = localStorage.getItem('lastAuthHttpStatus');
        // Clear after reading to avoid stale values on subsequent attempts
        if (lastStatus) localStorage.removeItem('lastAuthHttpStatus');

        let errorMessage = 'The Location Station server may be down. Try again later or contact support@locationstation.net.';
        if (lastStatus === '403') {
          const fallbackEmail = (() => {
            try { return localStorage.getItem('lastLoginEmail') || undefined; } catch { return undefined; }
          })();
          const email = user?.email || fallbackEmail || 'unknown@unknown';
          errorMessage = `Access denied for ${email}. Your Google account authenticated, but you are not authorized for CityHub. If you need access, contact your admin or support@locationstation.net.`;
        }
        console.error('Login error:', errorMessage);
        onError?.(errorMessage);
      } else {
        dispatch(setUser(user));
        onSuccess?.();
      }
    } catch (error: any) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      console.error('Login error:', errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      variant="contained"
      startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <GoogleIcon />}
      onClick={handleLogin}
      disabled={isLoading}
      sx={{
        backgroundColor: '#4285F4',
        color: 'white',
        textTransform: 'none',
        '&:hover': {
          backgroundColor: '#357ABD',
        },
        padding: '8px 16px',
      }}
    >
      {isLoading ? 'Signing in...' : 'Sign in with Google'}
    </Button>
  );
};

export default GoogleLoginButton;